import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Activity, AlertCircle, CheckCircle, Download, 
  RefreshCw, Wifi, WifiOff, Car, Gauge,
  AlertTriangle, FileText, Settings, Cpu, Monitor,
  ShoppingCart, Package, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Status Badge Component
const StatusBadge = ({ status, size = 'default' }) => {
  const getStatusStyle = () => {
    switch (status?.toLowerCase()) {
      case 'online':
      case 'connected':
      case 'approved':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'offline':
      case 'disconnected':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      case 'pending':
      case 'connecting':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'error':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <Badge className={`${getStatusStyle()} border ${size === 'large' ? 'text-sm px-3 py-1' : 'text-xs'}`}>
      {status || 'Unknown'}
    </Badge>
  );
};

// Connection Status Display
const ConnectionStatus = ({ label, status, icon: Icon, details }) => {
  const isConnected = status === 'online' || status === 'connected';
  
  return (
    <div className={`p-4 rounded-lg border ${isConnected ? 'border-green-500/30 bg-green-500/10' : 'border-gray-700 bg-gray-800/50'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${isConnected ? 'text-green-400' : 'text-gray-500'}`} />
          <span className="text-white font-medium">{label}</span>
        </div>
        <StatusBadge status={status} />
      </div>
      {details && (
        <p className="text-sm text-gray-400">{details}</p>
      )}
    </div>
  );
};

export const OBDLivePage = () => {
  const navigate = useNavigate();
  const { user, subscription } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Agent & Session state
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [latestVersion, setLatestVersion] = useState(null);
  
  // Dongle shop state
  const [dongleProducts, setDongleProducts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  
  // Feature flag - accepts multiple legacy/current key names
  const hasOBDFeature = !!(
    subscription?.features?.obd_live_diagnosis === true ||
    subscription?.features?.obd_live === true ||
    subscription?.features?.live_obd === true ||
    subscription?.features?.live_gps_tracking === true
  );

  // Fetch user's agent sessions
  const fetchSessions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/live-obd/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sessionList = response.data.sessions || [];
      setSessions(sessionList);
      
      // Find active session
      const active = sessionList.find(s => s.state === 'online');
      setActiveSession(active || null);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  }, []);

  // Fetch latest agent version
  const fetchLatestVersion = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dongle-shop/agent/latest`);
      setLatestVersion(response.data);
    } catch (error) {
      console.error('Failed to fetch agent version:', error);
    }
  }, []);

  // Fetch dongle products
  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dongle-shop/products?featured_only=true`);
      setDongleProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  }, []);

  // Fetch user purchases
  const fetchPurchases = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/dongle-shop/purchases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchases(response.data.purchases || []);
    } catch (error) {
      console.error('Failed to fetch purchases:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchSessions(),
        fetchLatestVersion(),
        fetchProducts(),
        fetchPurchases()
      ]);
      setLoading(false);
    };
    
    loadData();
    
    // Poll for session updates
    const interval = setInterval(fetchSessions, 10000);
    return () => clearInterval(interval);
  }, [fetchSessions, fetchLatestVersion, fetchProducts, fetchPurchases]);

  // Download agent - supports both GitHub URLs (full URL) and local DB paths
  const downloadAgent = () => {
    if (!latestVersion?.download_url) {
      toast.error('Keine Download-URL verfügbar');
      return;
    }
    const url = latestVersion.download_url;
    const fullUrl = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
    window.open(fullUrl, '_blank');
    toast.success(`Download gestartet (Version ${latestVersion.version || ''})`);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-DE');
  };

  // Compare semver versions: -1 if a < b, 0 if equal, 1 if a > b
  const compareVersions = (a, b) => {
    if (!a || !b) return 0;
    const pa = a.replace(/^v/i, '').split('.').map(n => parseInt(n) || 0);
    const pb = b.replace(/^v/i, '').split('.').map(n => parseInt(n) || 0);
    for (let i = 0; i < 3; i++) {
      if ((pa[i] || 0) < (pb[i] || 0)) return -1;
      if ((pa[i] || 0) > (pb[i] || 0)) return 1;
    }
    return 0;
  };

  // If user doesn't have OBD feature, show upgrade prompt
  if (!hasOBDFeature) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card className="bg-[#1a2332] border-gray-800">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-blue-500/20 rounded-full flex items-center justify-center">
              <Activity className="w-8 h-8 text-blue-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Live OBD Diagnose</h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Mit Live OBD können Sie Ihr Fahrzeug in Echtzeit diagnostizieren, 
              Fehlercodes auslesen und Live-Daten überwachen.
            </p>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left max-w-2xl mx-auto">
                <div className="p-4 bg-gray-800/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-400 mb-2" />
                  <h3 className="text-white font-medium">Echtzeit-Diagnose</h3>
                  <p className="text-gray-400 text-sm">Live-Daten direkt vom Fahrzeug</p>
                </div>
                <div className="p-4 bg-gray-800/50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-yellow-400 mb-2" />
                  <h3 className="text-white font-medium">Fehlercodes</h3>
                  <p className="text-gray-400 text-sm">DTCs auslesen und löschen</p>
                </div>
                <div className="p-4 bg-gray-800/50 rounded-lg">
                  <Gauge className="w-5 h-5 text-blue-400 mb-2" />
                  <h3 className="text-white font-medium">Sensor-Daten</h3>
                  <p className="text-gray-400 text-sm">Alle Sensoren überwachen</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/plans')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Plan upgraden
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-400" />
            Live OBD Diagnose
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Verbinden Sie Ihren OBD-Dongle für Echtzeit-Fahrzeugdiagnose
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchSessions}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
          {latestVersion?.download_url && (
            <Button onClick={downloadAgent} className="bg-blue-600 hover:bg-blue-700" data-testid="agent-download-btn">
              <Download className="w-4 h-4 mr-2" />
              Agent herunterladen {latestVersion.version && `v${latestVersion.version}`}
            </Button>
          )}
        </div>
      </div>

      {/* Update Available Banner - shown when active agent is older than latest */}
      {activeSession?.agent_version && latestVersion?.version &&
       compareVersions(activeSession.agent_version, latestVersion.version) < 0 && (
        <div className="bg-gradient-to-r from-amber-500/10 to-cyan-500/10 border border-amber-500/30 rounded-lg p-4 flex items-center gap-3" data-testid="update-banner">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-white font-medium text-sm">
              Update verfügbar: v{latestVersion.version}
              <span className="text-gray-400 ml-2 text-xs">(installiert: v{activeSession.agent_version})</span>
            </p>
            {latestVersion.release_notes && (
              <p className="text-gray-300 text-xs mt-1 line-clamp-2">{latestVersion.release_notes.substring(0, 200)}</p>
            )}
          </div>
          <Button onClick={downloadAgent} size="sm" className="bg-amber-500 hover:bg-amber-600 text-black">
            <Download className="w-3 h-3 mr-1" />
            Jetzt updaten
          </Button>
        </div>
      )}

      {/* Connection Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ConnectionStatus
          label="Desktop Agent"
          status={activeSession?.state || 'offline'}
          icon={Monitor}
          details={activeSession ? `Version ${activeSession.agent_version} • ${activeSession.os_info || 'Windows'}` : 'Agent nicht verbunden'}
        />
        <ConnectionStatus
          label="OBD Dongle"
          status={activeSession?.adapter_state || 'disconnected'}
          icon={Cpu}
          details={activeSession?.dongle_name || 'Kein Dongle erkannt'}
        />
        <ConnectionStatus
          label="Fahrzeug"
          status={activeSession?.vehicle_state || 'disconnected'}
          icon={Car}
          details={activeSession?.connected_vehicle_vin ? `VIN: ${activeSession.connected_vehicle_vin}` : 'Kein Fahrzeug verbunden'}
        />
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#1a2332] border border-gray-800">
          <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600">
            <Activity className="w-4 h-4 mr-2" />
            Übersicht
          </TabsTrigger>
          <TabsTrigger value="download" className="data-[state=active]:bg-blue-600">
            <Download className="w-4 h-4 mr-2" />
            Agent & Dongle
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-blue-600">
            <FileText className="w-4 h-4 mr-2" />
            Verlauf
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          {activeSession ? (
            <div className="space-y-4">
              {/* Active Session Info */}
              <Card className="bg-[#1a2332] border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Wifi className="w-5 h-5 text-green-400" />
                    Aktive Verbindung
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-gray-400 text-sm">Status</p>
                      <StatusBadge status={activeSession.state} size="large" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Agent Version</p>
                      <p className="text-white">{activeSession.agent_version}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Verbunden seit</p>
                      <p className="text-white">{formatDate(activeSession.connected_at)}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Letzter Heartbeat</p>
                      <p className="text-white">{formatDate(activeSession.last_heartbeat)}</p>
                    </div>
                  </div>
                  
                  {activeSession.vehicle_state === 'connected' && (
                    <div className="mt-6 pt-4 border-t border-gray-700">
                      <h3 className="text-white font-medium mb-3">Verbundenes Fahrzeug</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-gray-400 text-sm">VIN</p>
                          <p className="text-white font-mono">{activeSession.connected_vehicle_vin}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Marke</p>
                          <p className="text-white">{activeSession.connected_vehicle_brand || '-'}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Modell</p>
                          <p className="text-white">{activeSession.connected_vehicle_model || '-'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="bg-[#1a2332] border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white">Schnellaktionen</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2" disabled>
                      <AlertCircle className="w-5 h-5" />
                      <span>Fehlercodes lesen</span>
                    </Button>
                    <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2" disabled>
                      <Gauge className="w-5 h-5" />
                      <span>Live-Daten</span>
                    </Button>
                    <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2" disabled>
                      <FileText className="w-5 h-5" />
                      <span>Bericht erstellen</span>
                    </Button>
                    <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2" disabled>
                      <Settings className="w-5 h-5" />
                      <span>Einstellungen</span>
                    </Button>
                  </div>
                  <p className="text-gray-500 text-sm mt-4 text-center">
                    Weitere Funktionen werden in Kürze verfügbar sein.
                  </p>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="bg-[#1a2332] border-gray-800">
              <CardContent className="pt-8 pb-8 text-center">
                <WifiOff className="w-16 h-16 mx-auto mb-4 text-gray-500" />
                <h2 className="text-xl font-bold text-white mb-2">Kein Agent verbunden</h2>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  Laden Sie den Desktop-Agent herunter und starten Sie ihn, um Ihr Fahrzeug zu verbinden.
                </p>
                <div className="space-y-3">
                  <Button onClick={downloadAgent} className="bg-blue-600 hover:bg-blue-700">
                    <Download className="w-4 h-4 mr-2" />
                    Agent für Windows herunterladen
                  </Button>
                  <p className="text-gray-500 text-sm">
                    Version {latestVersion?.version || '1.0.0'} • Nur Windows
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Download & Dongle Tab */}
        <TabsContent value="download" className="mt-4 space-y-4">
          {/* Agent Download */}
          <Card className="bg-[#1a2332] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Download className="w-5 h-5 text-blue-400" />
                CarCortex OBD Agent
              </CardTitle>
              <CardDescription>
                Desktop-Anwendung für die Verbindung mit Ihrem OBD-Dongle
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                <div>
                  <h3 className="text-white font-medium">Windows Version</h3>
                  <p className="text-gray-400 text-sm">
                    Version {latestVersion?.version || '1.0.0'} • 
                    {latestVersion?.release_notes || 'Stabile Version'}
                  </p>
                </div>
                <Button onClick={downloadAgent} className="bg-blue-600 hover:bg-blue-700">
                  <Download className="w-4 h-4 mr-2" />
                  Download (.exe)
                </Button>
              </div>
              <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div>
                    <h4 className="text-yellow-400 font-medium">Systemanforderungen</h4>
                    <ul className="text-gray-400 text-sm mt-1 list-disc list-inside">
                      <li>Windows 10 oder höher</li>
                      <li>USB-Anschluss oder Bluetooth</li>
                      <li>Kompatibler OBD2-Dongle (ELM327, OBDLink, etc.)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dongle Shop */}
          <Card className="bg-[#1a2332] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-green-400" />
                OBD Dongle kaufen
              </CardTitle>
              <CardDescription>
                Empfohlene OBD2-Adapter für die beste Kompatibilität
              </CardDescription>
            </CardHeader>
            <CardContent>
              {dongleProducts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aktuell keine Produkte verfügbar</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {dongleProducts.map(product => (
                    <div key={product.id} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                      <div className="flex gap-4">
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.name} className="w-20 h-20 object-cover rounded" />
                        ) : (
                          <div className="w-20 h-20 bg-gray-700 rounded flex items-center justify-center">
                            <Cpu className="w-8 h-8 text-gray-500" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="text-white font-medium">{product.name}</h3>
                          <p className="text-gray-400 text-sm">{product.short_description || product.device_type}</p>
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xl font-bold text-green-400">{product.price}€</span>
                            {product.compare_at_price && (
                              <span className="text-gray-500 line-through text-sm">{product.compare_at_price}€</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button className="w-full mt-3 bg-green-600 hover:bg-green-700" disabled>
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Kaufen (Coming Soon)
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* User Purchases */}
          {purchases.length > 0 && (
            <Card className="bg-[#1a2332] border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Meine Bestellungen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {purchases.map(purchase => (
                    <div key={purchase.id} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                      <div>
                        <p className="text-white">{purchase.product_name}</p>
                        <p className="text-gray-400 text-sm">{formatDate(purchase.purchased_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-medium">{purchase.total_price}€</p>
                        <StatusBadge status={purchase.status} />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card className="bg-[#1a2332] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Verbindungsverlauf</CardTitle>
            </CardHeader>
            <CardContent>
              {sessions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Keine Verbindungen gefunden</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sessions.map(session => (
                    <div key={session.id} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {session.state === 'online' ? (
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        ) : (
                          <div className="w-2 h-2 bg-gray-500 rounded-full" />
                        )}
                        <div>
                          <p className="text-white">Agent v{session.agent_version}</p>
                          <p className="text-gray-400 text-sm">{session.os_info || 'Windows'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <StatusBadge status={session.state} />
                        <p className="text-gray-500 text-xs mt-1">
                          {session.state === 'online' ? 'Verbunden' : 'Getrennt'} {formatDate(session.last_heartbeat)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OBDLivePage;
